<template>
  <div class="date-select__wrapper">
    <ElSelect
      v-model="innerValue.day"
      class="date-select__day"
      data-testid="date-select__day-input"
    >
      <ElOption
        v-for="day in dayLimit"
        :key="day"
        :data-testid="`date-select__day-${day}`"
        :value="day"
      >
        <span>
          {{ day }}
        </span>
      </ElOption>
    </ElSelect>
    <ElSelect
      v-model="innerValue.month"
      class="date-select__month"
      data-testid="date-select__month-input"
      popper-class="date-select__popper"
    >
      <ElOption
        v-for="(month, index) in monthLimit"
        :key="index"
        :data-testid="`date-select__month-${month}`"
        :label="month"
        :value="index"
      >
        <span>
          {{ month }}
        </span>
      </ElOption>
    </ElSelect>
    <ElSelect
      v-model="innerValue.year"
      class="date-select__year"
      data-testid="date-select__year-input"
      popper-class="date-select__popper"
    >
      <ElOption
        v-for="year in years"
        :key="year"
        :data-testid="`date-select__year-${year}`"
        :value="year"
      >
        <span>
          {{ year }}
        </span>
      </ElOption>
    </ElSelect>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import moment from "moment";
import { generateYearsRange } from "@/services/utils/date";

interface IFoundedDate {
  day: number;
  month: number;
  year: number;
}

export default defineComponent({
  name: "PxDateSelect",
  props: {
    /**
     * Number to render inside the select.
     */
    modelValue: {
      default: "",
      type: String,
    },
  },

  data() {
    return {
      innerValue: {
        day: 0,
        month: 2,
        year: 0,
      } as IFoundedDate,

      months: moment.months(),
      years: generateYearsRange(1930),
    };
  },

  computed: {
    /**
     * Number of days in the selected month.
     */
    dayLimit(): number {
      const currentDate = moment();

      if (
        currentDate.month() === this.innerValue.month &&
        currentDate.year() === this.innerValue.year
      ) {
        return currentDate.date();
      }

      return moment(
        `${this.innerValue.year}-${this.innerValue.month + 1}`,
        "YYYY-MM",
      ).daysInMonth();
    },

    /**
     * Number of days in the selected month.
     */
    monthLimit(): string[] {
      const currentDate = moment();

      if (currentDate.year() === this.innerValue.year) {
        return this.months.filter((item, index) => {
          return currentDate.month() >= index;
        });
      }

      return this.months;
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newVal: string, oldVal: string) {
        if (newVal === oldVal) {
          return;
        }

        this.setDate();
      },
    },

    innerValue: {
      deep: true,
      handler(newVal: IFoundedDate) {
        const selectedMonth = newVal.month + 1;
        const lastMonth = this.monthLimit.length - 1;
        let selectedDay = newVal.day;

        if (selectedMonth > lastMonth) {
          newVal.month = lastMonth;
        }

        if (selectedDay > this.dayLimit) {
          selectedDay = this.dayLimit;
          newVal.day = selectedDay;
        }

        const date = moment(
          `${newVal.year}-${selectedMonth}-${selectedDay}`,
          "YYYY-MM-DD",
        ).format("YYYY-MM-DD");

        if (date !== this.modelValue) {
          this.$emit("update:modelValue", date);
          this.$emit("change", date);

          this.innerValue = { ...newVal };
        }
      },
    },
  },

  methods: {
    /**
     * Set the founded date to be edited by the form.
     */
    setDate() {
      const parsedDate = this.modelValue
        ? moment(this.modelValue, "YYYY-MM-DD")
        : moment();

      this.innerValue = {
        day: parsedDate.date(),
        month: parsedDate.month(),
        year: parsedDate.year(),
      };
    },
  },
});
</script>

<style lang="scss" scoped>
.date-select {
  &__day {
    max-width: 78px;
  }

  &__month {
    max-width: 121px;
  }

  &__year {
    max-width: 91px;
  }
}

.date-select__wrapper {
  display: flex;
  flex-direction: row;
  width: 100%;

  @include breakpoint-up(lg) {
    justify-content: space-between;
  }

  :deep() .el-select {
    @include breakpoint-up(sm) {
      margin-right: 10px;
    }
  }

  :deep() .el-input__inner {
    @include breakpoint-down(md) {
      padding-left: 10px;
    }
  }

  :deep() .el-input__suffix {
    @include breakpoint-down(md) {
      right: 8px;
    }
  }
}
</style>
